/**
 * App.js — Construtora Carvalho
 *
 * Site institucional da Construtora Carvalho (Capelinha-MG).
 * Single Page Application em React sem dependências externas de roteamento.
 * A navegação entre páginas é controlada pelo estado `page` no componente App.
 *
 * Estrutura de páginas:
 *   inicio          → PageHome
 *   sobre           → PageSobre
 *   empreendimentos → PageEmpreendimentos
 *   empreendimento  → PageEmpreendimento (detalhe de um item)
 *   servicos        → PageServicos
 *   contato         → PageContato
 *
 * Para adicionar fotos aos empreendimentos, veja o comentário em EMPREENDIMENTOS.
 */

import { useState, useEffect } from "react";
import joseAmaroImg from "./assets/joseamaro.png";
import equipeHeroImg from "./assets/EQUIPE.jpg";
import logoImg from "./assets/logo.png";
import belvedere1Img from "./assets/belvedere1.jpeg";
import belvedere3Img from "./assets/belvedere3.jpg";
import belvedere4Img from "./assets/belvedere4.jpg";
import belvedere5Img from "./assets/belvedere5.jpeg";
import belvedere6Img from "./assets/belvedere6.jpeg";
import cidadeJardimImg from "./assets/Cidade jardim.jpg";
import cidadeJardim2Img from "./assets/Cidade jardim2.jpg";
import cidadeJardim3Img from "./assets/Cidade jardim3.jpg";
import esplanadaImg from "./assets/Explanada.jpg";
import esplanada2Img from "./assets/explanada2.jpg";
import esplanada3Img from "./assets/explanada3.jpg";
import esplanada4Img from "./assets/explanada4.jpg";
import esplanada5Img from "./assets/explanada5.jpg";
import esplanada6Img from "./assets/explanada6.jpg";
import florestaImg from "./assets/Floresta.jpg";
import floresta2Img from "./assets/floresta2.jpg";
import floresta3Img from "./assets/floresta3.jpg";
import floresta4Img from "./assets/floresta4.jpg";
import floresta5Img from "./assets/floresta5.jpg";
import grajau1Img from "./assets/grajau1.jpg";
import grajau2Img from "./assets/grajau2.jpg";
import grajau3Img from "./assets/grajau3.jpg";
import grajau4Img from "./assets/grajau4.jpg";
import grajau5Img from "./assets/grajau5.jpg";
import grajau6Img from "./assets/grajau6.jpg";
import grajau7Img from "./assets/grajau7.jpg";
import nacoesImg from "./assets/nações.jpg";
import nacoe1Img from "./assets/nacoe1.jpg";
import nacoes2Img from "./assets/nacoes2.jpg";
import nacoes3Img from "./assets/nacoes3.jpg";
import nacoes4Img from "./assets/nacoes4.jpg";

// ─── HOOK RESPONSIVIDADE ──────────────────────────────────────────────────────

/**
 * useIsMobile — detecta se a tela está abaixo do breakpoint informado.
 * @param {number} bp - breakpoint em px (padrão: 768)
 * @returns {boolean} true quando a largura da janela for menor que bp
 */
function useIsMobile(bp = 768) {
  const [mob, setMob] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const h = () => setMob(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h); // limpa o listener ao desmontar
  }, [bp]);
  return mob;
}

// ─── DADOS ───────────────────────────────────────────────────────────────────

/**
 * EMPREENDIMENTOS — lista de todos os bairros/loteamentos da Construtora Carvalho.
 *
 * Campos:
 *   id     {number}      - identificador único
 *   nome   {string}      - nome do bairro
 *   tipo   {string}      - "Loteamento" | "Bairro Planejado"
 *   status {string}      - "Entregue e ocupado" | "Em construção"
 *                          OBS: empreendimentos com status "Em construção" recebem
 *                          destaque especial na seção de empreendimentos (card grande
 *                          com CTAs de venda de lotes).
 *   desc   {string}      - descrição curta exibida nos cards
 *   img    {any|null}    - imagem principal importada ou null (exibe placeholder)
 *   fotos  {any[]}       - galeria opcional para slideshow automatico nos cards
 *
 * Para adicionar fotos:
 *   1. Coloque o arquivo em src/assets/
 *   2. Importe no topo: import moradaNovaImg from "./assets/morada-nova.jpg";
 *   3. Substitua null pelo import: { ..., img: moradaNovaImg }
 */
const EMPREENDIMENTOS = [
  {
    id: 1, nome: "Morada Nova", tipo: "Loteamento", status: "Entregue e ocupado", img: null,
    ano: "2012",
    desc: "Primeiro grande loteamento da construtora — 200 lotes que marcaram o início de um novo ciclo de desenvolvimento urbano em Capelinha.",
    descCompleta: "O Morada Nova marcou o início de um novo ciclo de desenvolvimento urbano em Capelinha, sendo um dos primeiros projetos da construtora voltados à loteamentos e expansão planejada da cidade. Com 200 lotes, o bairro contribuiu para o crescimento ordenado da região, oferecendo infraestrutura essencial e qualidade de vida aos primeiros moradores.",
    diferenciais: ["Primeiro grande projeto de loteamento da construtora", "200 lotes planejados", "Infraestrutura básica consolidada", "Bairro já habitado e estabilizado"],
  },
  {
    id: 2, nome: "Jardim Aeroporto II", tipo: "Loteamento", status: "Entregue e ocupado", img: null,
    ano: "2013",
    desc: "Extensão estratégica de um dos eixos urbanos mais dinâmicos de Capelinha — 105 lotes com localização privilegiada e alta integração à cidade.",
    descCompleta: "O Jardim Aeroporto II deu continuidade ao processo de expansão urbana planejada em Capelinha, sendo a extensão de um dos eixos urbanos mais estratégicos da cidade, com forte crescimento populacional e presença comercial. Com 105 lotes, o bairro se destaca pela praticidade e pela integração com regiões já consolidadas da cidade.",
    diferenciais: ["105 lotes bem distribuídos", "Localização estratégica", "Integração com áreas urbanas existentes", "Projeto funcional e acessível", "Infraestrutura básica consolidada", "Bairro já habitado e estabilizado"],
  },
  {
    id: 3, nome: "Cidade Jardim", tipo: "Bairro Planejado", status: "Entregue e ocupado", img: cidadeJardimImg, fotos: [cidadeJardimImg, cidadeJardim2Img, cidadeJardim3Img],
    ano: "2014",
    desc: "220 lotes com organização urbana exemplar — ruas bem definidas e ambiente residencial tranquilo em constante valorização.",
    descCompleta: "O Cidade Jardim representa um importante avanço no conceito de loteamento planejado em Capelinha. Com 220 lotes, o bairro foi desenvolvido com foco em organização urbana, oferecendo ruas bem definidas e um ambiente ideal para moradia com qualidade.",
    diferenciais: ["220 lotes planejados", "Malha urbana organizada", "Bairro em constante valorização", "Ambiente residencial tranquilo", "Infraestrutura básica consolidada", "Bairro já habitado e estabilizado"],
  },
  {
    id: 4, nome: "Esplanada", tipo: "Bairro Planejado", status: "Entregue e ocupado", img: esplanadaImg, fotos: [esplanadaImg, esplanada2Img, esplanada3Img, esplanada4Img, esplanada5Img, esplanada6Img],
    ano: "2016",
    desc: "312 lotes em uma das regiões mais valorizadas de Capelinha — padrão construtivo elevado e infraestrutura consolidada.",
    descCompleta: "O Esplanada é um dos empreendimentos mais relevantes da construtora em Capelinha, localizado em uma das regiões mais valorizadas da cidade. Com 312 lotes, o bairro se destaca pelo padrão construtivo elevado, infraestrutura consolidada e forte valorização ao longo dos anos.",
    diferenciais: ["312 lotes", "Região nobre e valorizada", "Alto padrão construtivo", "Infraestrutura completa e consolidada"],
  },
  {
    id: 5, nome: "Nações", tipo: "Bairro Planejado", status: "Entregue e ocupado", img: nacoesImg, fotos: [nacoesImg, nacoe1Img, nacoes2Img, nacoes3Img, nacoes4Img],
    ano: "2018",
    desc: "250 lotes que equilibram custo-benefício, localização e qualidade urbana — nova polo de crescimento em Capelinha.",
    descCompleta: "O Bairro Nações reforça a atuação da construtora na criação de novos polos de crescimento em Capelinha. Com 250 lotes, o empreendimento foi planejado para oferecer equilíbrio entre custo-benefício, localização e qualidade urbana.",
    diferenciais: ["250 lotes", "Excelente custo-benefício", "Expansão urbana planejada", "Boa aceitação no mercado local", "Infraestrutura básica consolidada", "Bairro já habitado e estabilizado"],
  },
  {
    id: 6, nome: "Grajaú", tipo: "Bairro Planejado", status: "Entregue e ocupado", img: grajau1Img, fotos: [grajau1Img, grajau2Img, grajau3Img, grajau4Img, grajau5Img, grajau6Img, grajau7Img],
    ano: "2020",
    desc: "412 lotes — extensão direta do Nações, com grande escala, crescimento estruturado e alto potencial de valorização.",
    descCompleta: "Sendo uma extensão do bairro Nações, também construído pela Construtora Carvalho, o Grajaú representa uma nova fase de expansão com um projeto robusto de 412 lotes. O bairro foi desenvolvido com foco em escala, organização e potencial de valorização, atendendo à crescente demanda por novos espaços nessa região específica da cidade.",
    diferenciais: ["412 lotes", "Projeto de grande escala", "Alto potencial de valorização", "Crescimento estruturado", "Expansão do Nações, também da Construtora Carvalho", "Infraestrutura básica consolidada", "Bairro já habitado e estabilizado"],
  },
  {
    id: 7, nome: "Floresta", tipo: "Loteamento", status: "Entregue e ocupado", img: florestaImg, fotos: [florestaImg, floresta2Img, floresta3Img, floresta4Img, floresta5Img],
    ano: "2024",
    desc: "640 lotes — um dos maiores e mais modernos projetos da construtora, com crescimento expressivo: mais de 60% habitado em menos de 2 anos.",
    descCompleta: "O Floresta é um dos projetos mais recentes da construtora em Capelinha e também um dos maiores, com 640 lotes planejados para atender à expansão moderna da cidade. O bairro combina escala, organização e visão de futuro, consolidando um novo padrão de desenvolvimento urbano em uma nova região de Capelinha.",
    diferenciais: ["640 lotes", "Grande expansão urbana", "Planejamento moderno", "Alto potencial de crescimento", "Infraestrutura básica consolidada", "Mais de 60% habitado em menos de 2 anos"],
  },
  {
    id: 8, nome: "Belvedere", tipo: "Loteamento", status: "Em construção", img: belvedere3Img, fotos: [belvedere3Img, belvedere4Img, belvedere1Img, belvedere5Img, belvedere6Img],
    ano: "2026",
    desc: "760 lotes — o maior e mais ambicioso projeto da construtora, um novo marco urbano que transformou uma das principais entradas de Capelinha.",
    descCompleta: "O Belvedere é o mais novo e ambicioso projeto da construtora em Capelinha. Com 760 lotes, o empreendimento foi concebido para representar um novo marco de desenvolvimento urbano — um loteamento de alto padrão em uma nova região que reúne planejamento moderno, localização estratégica e alto potencial de valorização. Um projeto que deu uma nova cara a uma das principais entradas da cidade de Capelinha MG.",
    diferenciais: ["760 lotes — maior projeto da construtora", "Planejamento urbano de nova geração", "Alto potencial de valorização", "Projeto em fase de desenvolvimento", "Projetado para ser um loteamento de alto nível", "Nova cara para uma das principais entradas da cidade"],
  },
].sort((a, b) => b.id - a.id);

/**
 * SERVICOS — serviços oferecidos pela construtora exibidos na seção de serviços.
 *
 * Campos:
 *   titulo {string}   - nome do serviço
 *   desc   {string}   - parágrafo descritivo
 *   items  {string[]} - lista de diferenciais/funcionalidades exibida abaixo da descrição
 */
const SERVICOS = [
  {
    titulo: "Loteamentos",
    desc: "Transformamos áreas em bairros estruturados, com planejamento completo e infraestrutura pronta para valorização. Atuamos desde a viabilização da gleba até a execução final, incluindo asfalto, saneamento e redes elétricas.",
    items: ["Viabilização e regularização da gleba", "Asfalto, saneamento e redes elétricas", "Planejamento urbanístico completo", "Espaços prontos para o futuro"],
  },
  {
    titulo: "Construção",
    desc: "Projetos residenciais executados com eficiência, qualidade e foco na valorização do investimento do cliente. Cada obra é conduzida com rigor técnico e atenção aos detalhes.",
    items: ["Projetos arquitetônicos e estruturais", "Construção de residências", "Rigor técnico e atenção aos detalhes", "Conformidade com NBRs e normas municipais"],
  },
];

// ─── ÍCONES SVG ───────────────────────────────────────────────────────────────

/**
 * LogoIcon — marca da Construtora Carvalho.
 * Usado na navbar e no footer.
 * @param {number} size - largura em px (altura é calculada proporcionalmente)
 */
function LogoIcon({ size = 44 }) {
  return (
    <img
      src={logoImg}
      alt="Construtora Carvalho"
      width={size}
      height={Math.round(size * 0.86)}
      style={{ display: "block", objectFit: "contain" }}
    />
  );
}

/**
 * HouseIcon — ícone de casa SVG usado como placeholder nos cards de empreendimentos.
 * @param {string} color - cor de preenchimento (hex ou CSS)
 */
function HouseIcon({ color = "#CC1111" }) {
  return (
    <svg width="36" height="32" viewBox="0 0 36 32" fill="none">
      <polygon points="4,24 18,6 32,24" fill={color} opacity="0.18" />
      <polygon points="4,24 18,6 32,24" fill="none" stroke={color} strokeWidth="1.8" />
      <rect x="4" y="23" width="28" height="8" rx="1" fill={color} opacity="0.12" />
      <rect x="14" y="16" width="8" height="7" rx="0.5" fill={color} opacity="0.65" />
      <line x1="18" y1="16" x2="18" y2="23" stroke="white" strokeWidth="0.8" opacity="0.6" />
      <line x1="14" y1="19.5" x2="22" y2="19.5" stroke="white" strokeWidth="0.8" opacity="0.6" />
    </svg>
  );
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────

/**
 * Navbar — barra de navegação fixa no topo.
 * No desktop: links horizontais + botão "Fale conosco".
 * No mobile: ícone hambúrguer que expande um menu dropdown.
 *
 * @param {string}   activePage - chave da página atual (ex: "inicio")
 * @param {Function} setPage    - navega para outra página
 */
function Navbar({ activePage, setPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fecha o menu ao trocar de página
  const nav = (p) => { setPage(p); setMenuOpen(false); };

  const links = ["inicio", "sobre", "empreendimentos", "servicos", "contato"];
  const labels = { inicio: "Início", sobre: "Sobre", empreendimentos: "Empreendimentos", servicos: "Serviços", contato: "Contato" };

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      background: scrolled ? "rgba(255,255,255,0.97)" : "#fff",
      borderBottom: "3px solid #CC1111",
      boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.08)" : "none",
      transition: "box-shadow 0.3s",
      fontFamily: "'Barlow', sans-serif",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 1.25rem", height: 78, display: "flex", alignItems: "center", justifyContent: "space-between" }}>

        {/* Logo */}
        <button onClick={() => nav("inicio")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <LogoIcon size={52} />
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#CC1111", letterSpacing: 0.3 }}>Construtora</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#CC1111", letterSpacing: 0.3 }}>Carvalho</div>
          </div>
        </button>

        {isMobile ? (
          /* ── Hambúrguer ── */
          <button
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "10px 8px", display: "flex", flexDirection: "column", gap: 5, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}
          >
            <span style={{ display: "block", width: 24, height: 2, background: "#333", transition: "all 0.25s", transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
            <span style={{ display: "block", width: 24, height: 2, background: "#333", transition: "all 0.25s", opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 24, height: 2, background: "#333", transition: "all 0.25s", transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
          </button>
        ) : (
          /* ── Links desktop ── */
          <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
            {links.map(l => (
              <button key={l} onClick={() => nav(l)} style={{
                background: "none", border: "none", cursor: "pointer", padding: "4px 0",
                fontSize: 13, fontWeight: activePage === l ? 600 : 400,
                color: activePage === l ? "#CC1111" : "#444",
                borderBottom: activePage === l ? "2px solid #CC1111" : "2px solid transparent",
                transition: "color 0.2s, border-color 0.2s",
                fontFamily: "'Barlow', sans-serif",
              }}>{labels[l]}</button>
            ))}
            <button onClick={() => nav("contato")} style={{
              background: "#1B6B2F", color: "#fff", border: "none",
              padding: "9px 20px", borderRadius: 4, fontSize: 13,
              fontWeight: 500, cursor: "pointer", fontFamily: "'Barlow', sans-serif",
            }}>Fale conosco</button>
          </div>
        )}
      </div>

      {/* ── Menu mobile dropdown ── */}
      {isMobile && menuOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #f0f0f0",
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
          padding: "8px 0 16px",
        }}>
          {links.map(l => (
            <button key={l} onClick={() => nav(l)} style={{
              display: "block", width: "100%", background: "none", border: "none",
              textAlign: "left", padding: "14px 24px", fontSize: 15, minHeight: 44,
              fontWeight: activePage === l ? 600 : 400,
              color: activePage === l ? "#CC1111" : "#333",
              fontFamily: "'Barlow', sans-serif", cursor: "pointer",
              borderLeft: activePage === l ? "3px solid #CC1111" : "3px solid transparent",
            }}>{labels[l]}</button>
          ))}
          <div style={{ padding: "8px 20px 0" }}>
            <button onClick={() => nav("contato")} style={{
              width: "100%", background: "#1B6B2F", color: "#fff", border: "none",
              padding: "14px", borderRadius: 4, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
            }}>Fale conosco</button>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── SEÇÕES DA HOME ───────────────────────────────────────────────────────────

/**
 * Hero — seção principal da Home (above the fold).
 * Exibe headline institucional, subtítulo, botões de CTA e badges de autoridade
 * (7 bairros entregues, presença consolidada, novo empreendimento).
 *
 * @param {Function} setPage - navega para outra página ao clicar nos CTAs
 */
const HERO_FOTOS = [equipeHeroImg, belvedere1Img, belvedere5Img];

function Hero({ setPage }) {
  const isMobile = useIsMobile();
  const navHeight = 81;
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setSlideIndex((index) => index + 1), 3600);
    return () => window.clearInterval(timer);
  }, []);

  const currentIndex = slideIndex % HERO_FOTOS.length;

  const heroBadges = isMobile
    ? [
        { icon: "🏘️", label: "7 bairros entregues", bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.22)" },
        { icon: "✦", label: "Presença em Capelinha", bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.22)" },
        { icon: "⊕", label: "Novo empreendimento", bg: "#CC1111", border: "transparent" },
      ]
    : [
        { icon: "🏘️", label: "7 bairros entregues", bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.22)" },
        { icon: "✦", label: "Presença consolidada em Capelinha", bg: "rgba(255,255,255,0.12)", border: "rgba(255,255,255,0.22)" },
        { icon: "⊕", label: "Novo empreendimento em desenvolvimento", bg: "#CC1111", border: "transparent" },
      ];

  return (
    <section style={{
      position: "relative",
      height: "100vh",
      minHeight: 580,
      overflow: "hidden",
      fontFamily: "'Barlow', sans-serif",
    }}>
      {/* Slideshow de fundo */}
      <div style={{ position: "absolute", left: 0, right: 0, top: navHeight, bottom: -navHeight, overflow: "hidden" }}>
        {HERO_FOTOS.map((foto, index) => (
          <img
            key={index}
            src={foto}
            alt=""
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center top",
              opacity: index === currentIndex ? 1 : 0,
              transition: "opacity 1.2s ease",
            }}
          />
        ))}
      </div>

      {/* Overlay horizontal: escuro à esquerda, translúcido à direita */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to right, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.62) 55%, rgba(0,0,0,0.42) 100%)",
      }} />
      {/* Overlay vertical: escurece base */}
      <div style={{
        position: "absolute", inset: 0,
        background: "linear-gradient(to top, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0.18) 100%)",
      }} />

      {/* Conteúdo fixado no lado esquerdo/baixo com espaço real abaixo dos badges */}
      <div style={{
        position: "absolute", zIndex: 1,
        bottom: isMobile ? 84 : 160,
        left: isMobile ? 22 : "5vw",
        right: isMobile ? 22 : 0,
        maxWidth: isMobile ? "100%" : 780,
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Tag de localização */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <div style={{ width: 32, height: 2, background: "#CC1111", borderRadius: 1 }} />
          <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: "2px",
            color: "rgba(255,255,255,0.82)", textTransform: "uppercase",
          }}>
            Capelinha, Minas Gerais
          </span>
        </div>

        {/* Título */}
        <h1 style={{
          fontSize: isMobile ? "clamp(30px, 7.6vw, 34px)" : "clamp(36px, 4.5vw, 58px)",
          fontWeight: 800, color: "#fff", lineHeight: isMobile ? 1.12 : 1.18,
          marginBottom: isMobile ? 16 : 20, marginTop: 0,
          textShadow: "0 2px 16px rgba(0,0,0,0.4)",
        }}>
          {isMobile ? "Bairros que moldam o " : <>Construindo os bairros que<br />moldam o{" "}</>}
          <em style={{
            fontStyle: "italic",
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontWeight: 400,
            color: "#6ECE8C",
          }}>futuro</em>{" "}
          de Capelinha.
        </h1>

        {/* Subtítulo */}
        <p style={{
          fontSize: isMobile ? 14 : 15, color: "rgba(255,255,255,0.72)",
          lineHeight: isMobile ? 1.55 : 1.72, marginBottom: isMobile ? 26 : 40, marginTop: 0,
          maxWidth: isMobile ? 340 : 460,
        }}>
          {isMobile
            ? "Loteamentos planejados que impulsionam o crescimento da nossa região."
            : "Referência em urbanismo e construção. A Construtora Carvalho projeta e executa loteamentos que impulsionam e definem o crescimento da nossa região."}
        </p>

        {/* Badges */}
        <div style={{ display: "flex", gap: isMobile ? 8 : 10, flexWrap: "wrap", maxWidth: isMobile ? 330 : "none" }}>
          {heroBadges.map(({ icon, label, bg, border }) => (
            <div key={label} style={{
              display: "flex", alignItems: "center", gap: 8,
              background: bg,
              border: `1px solid ${border}`,
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              padding: isMobile ? "8px 12px" : "9px 16px", borderRadius: 4,
            }}>
              <span style={{ fontSize: isMobile ? 12 : 13 }}>{icon}</span>
              <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, color: "#fff", whiteSpace: "nowrap" }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Indicador de scroll */}
      <div style={{
        position: "absolute", bottom: 24, right: 32, zIndex: 1,
        display: isMobile ? "none" : "flex", flexDirection: "column", alignItems: "center", gap: 6,
      }}>
        <span style={{ fontSize: 9, letterSpacing: "2px", color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>Scroll</span>
        <div style={{ width: 1, height: 32, background: "linear-gradient(to bottom, rgba(255,255,255,0.45), transparent)" }} />
      </div>
    </section>
  );
}

/**
/**
 * TiraInstitucional — tira de dois blocos horizontais com texto institucional.
 * Esquerda: parágrafo curto sobre a história da empresa.
 * Direita: frase de posicionamento em destaque + descrição complementar.
 * Em mobile os blocos empilham verticalmente.
 */
function TiraInstitucional() {
  const isMobile = useIsMobile();
  return (
    <section style={{
      background: "#ffffff",
      padding: isMobile ? "52px 1.25rem" : "72px 2rem",
      fontFamily: "'Barlow', sans-serif",
    }}>
      <div style={{
        maxWidth: 1000, margin: "0 auto",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1px 1fr",
        gap: isMobile ? 36 : 0,
        alignItems: "center",
      }}>
        {/* Coluna esquerda — texto institucional */}
        <div style={{ padding: isMobile ? 0 : "0 48px 0 0" }}>
          <div style={{ width: 32, height: 3, background: "#CC1111", marginBottom: 20 }} />
          <p style={{ fontSize: 15, color: "#444", lineHeight: 1.85 }}>
            Com raízes em Capelinha, a Construtora Carvalho consolidou-se no mercado mineiro através
            de uma gestão técnica e um olhar estratégico sobre o desenvolvimento urbano. Nossa história
            é construída em cada bairro entregue e em cada projeto transformado em lar.
          </p>
        </div>

        {/* Divisor vertical */}
        {!isMobile && <div style={{ width: 1, background: "#e8e4df", alignSelf: "stretch" }} />}

        {/* Coluna direita — frase + descrição complementar */}
        <div style={{ padding: isMobile ? 0 : "0 0 0 48px" }}>
          <blockquote style={{
            fontSize: "clamp(16px, 2.2vw, 20px)", fontWeight: 600,
            color: "#1B6B2F", lineHeight: 1.4,
            borderLeft: "3px solid #CC1111", paddingLeft: 18,
            margin: "0 0 20px",
            fontStyle: "italic",
          }}>
            "Quem constrói com propósito, constrói para durar."
          </blockquote>
          <p style={{ fontSize: 14, color: "#777", lineHeight: 1.8, margin: 0 }}>
            A Construtora Carvalho nasceu com um objetivo claro: contribuir com o crescimento organizado
            de Capelinha, criando espaços que valorizam pessoas, famílias e o futuro.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * SecaoDiferencial — seção "Por que escolher a Construtora Carvalho".
 * Exibe 3 cards com os diferenciais da empresa + botão CTA para contato.
 *
 * @param {Function} setPage - navega para a página de contato ao clicar no CTA
 */
function SecaoDiferencial({ setPage }) {
  const isMobile = useIsMobile();
  const diferenciais = [
    { titulo: "Presença consolidada em Capelinha",   desc: "Décadas de atuação na região, com bairros que hoje fazem parte da cidade." },
    { titulo: "Histórico comprovado de entregas",    desc: "7 bairros entregues com infraestrutura completa e documentação regularizada." },
    { titulo: "Compromisso com qualidade e regularização", desc: "Cada projeto segue rigor técnico e documental do início à entrega." },
  ];
  return (
    <section style={{
      background: "#d6e8d9",
      padding: isMobile ? "52px 1.25rem" : "72px 2rem",
      fontFamily: "'Barlow', sans-serif",
    }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "1.5px", color: "#CC1111", textTransform: "uppercase", marginBottom: 12 }}>
            Por que nos escolher
          </div>
          <h2 style={{ fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3 }}>
            Por que escolher a Construtora Carvalho
          </h2>
          <div style={{ width: 40, height: 3, background: "#CC1111", margin: "14px auto 0" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 20, marginBottom: 40 }}>
          {diferenciais.map(({ titulo, desc }) => (
            <div key={titulo} style={{
              background: "#fff",
              border: "0.5px solid #e0e8e2",
              borderTop: "3px solid #1B6B2F",
              borderRadius: 8,
              padding: "24px 22px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#1B6B2F", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <span style={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</span>
              </div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 8, lineHeight: 1.4 }}>{titulo}</h3>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.7, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <button onClick={() => setPage("contato")} style={{
            background: "#CC1111", color: "#fff",
            padding: "14px 36px", border: "none", borderRadius: 4,
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            fontFamily: "'Barlow', sans-serif",
            boxShadow: "0 4px 16px rgba(204,17,17,0.3)", minHeight: 44,
          }}>
            Fale com nossa equipe
          </button>
        </div>
      </div>
    </section>
  );
}

function getEmpreendimentoFotos(empreendimento) {
  return empreendimento.fotos?.length ? empreendimento.fotos : (empreendimento.img ? [empreendimento.img] : []);
}

function EmpreendimentoSlideshow({ empreendimento, activeIndex, borderColor = "#1B6B2F", dim = false, borderRadius = 0 }) {
  const fotos = getEmpreendimentoFotos(empreendimento);
  const currentIndex = fotos.length ? activeIndex % fotos.length : 0;

  return (
    <>
      {fotos.length ? (
        fotos.map((foto, index) => (
          <img
            key={`${empreendimento.id}-${index}`}
            src={foto}
            alt={empreendimento.nome}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: index === currentIndex ? (dim ? 0.8 : 1) : 0,
              transition: "opacity 0.8s ease",
              borderRadius,
            }}
          />
        ))
      ) : (
        <div style={{ position: "absolute", inset: 0, background: `${borderColor}0D`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <HouseIcon color={borderColor} />
          {dim && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.5px" }}>Imagens em breve</span>}
        </div>
      )}

      {fotos.length > 1 && (
        <div style={{ position: "absolute", left: 12, bottom: 10, display: "flex", gap: 5, zIndex: 1 }}>
          {fotos.map((_, index) => (
            <span
              key={index}
              style={{
                width: index === currentIndex ? 18 : 6,
                height: 6,
                borderRadius: 999,
                background: index === currentIndex ? "#fff" : "rgba(255,255,255,0.45)",
                transition: "width 0.25s ease, background 0.25s ease",
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * SecaoEmpreendimentos — exibe todos os empreendimentos da construtora.
 *
 * Lógica de destaque:
 *   - Empreendimentos com status "Em construção" aparecem primeiro, em card
 *     destacado de largura total com CTAs de venda (WhatsApp + interesse).
 *   - Empreendimentos entregues aparecem abaixo em grid compacto.
 *
 * @param {Function} setPage               - navega para outra página
 * @param {Function} setEmpreendimentoAtivo - define o empreendimento ativo para a página de detalhe
 */
function SecaoEmpreendimentos({ setPage, setEmpreendimentoAtivo }) {
  const isMobile = useIsMobile();
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setSlideIndex((index) => index + 1), 3600);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section style={{ padding: isMobile ? "48px 1.25rem" : "72px 2rem", fontFamily: "'Barlow', sans-serif", background: "#ffffff" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "1.5px", color: "#CC1111", textTransform: "uppercase", marginBottom: 12 }}>Empreendimentos</div>
          <h2 style={{ fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3 }}>Nossa marca no mapa</h2>
          <div style={{ width: 40, height: 3, background: "#CC1111", margin: "14px auto 20px" }} />
          <p style={{ fontSize: 15, color: "#666", maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            Mais do que empreendimentos, entregamos resultados de décadas de trabalho. Desenvolvemos bairros
            que hoje fazem parte da história de Capelinha. Conheça os projetos que levam a assinatura da Construtora Carvalho.
          </p>
        </div>

        {/* ── Destaque: empreendimentos em desenvolvimento ── */}
        {EMPREENDIMENTOS.filter(e => e.status === "Em construção").map(e => (
          <div key={e.id} style={{
            marginBottom: 32,
            background: "#1a1a1a",
            borderRadius: 10,
            overflow: "hidden",
            border: "2px solid #CC1111",
            boxShadow: "0 8px 32px rgba(204,17,17,0.15)",
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr",
            }}>
              {/* Topo: badge + título */}
              <div style={{ padding: "clamp(16px, 4vw, 28px) clamp(16px, 4vw, 28px) 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  <span style={{
                    background: "#CC1111", color: "#fff",
                    fontSize: 10, fontWeight: 700, letterSpacing: "1.5px",
                    textTransform: "uppercase", padding: "4px 12px", borderRadius: 2,
                  }}>
                    ● Em desenvolvimento
                  </span>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
                    Oportunidade de investimento — lotes disponíveis
                  </span>
                </div>
                <h3 style={{ fontSize: "clamp(22px, 4vw, 32px)", fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                  {e.nome}
                </h3>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.75, maxWidth: 560, margin: "0 0 24px" }}>
                  {e.desc}
                </p>
              </div>

              {/* Área de imagem */}
              <div style={{ position: "relative", width: "100%", paddingTop: isMobile ? "50%" : "35%", background: "#111", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <EmpreendimentoSlideshow empreendimento={e} activeIndex={slideIndex} borderColor="#CC1111" dim />
              </div>

              {/* Rodapé com CTA */}
              <div style={{
                padding: "16px clamp(16px, 4vw, 28px)",
                background: "#111",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 12,
              }}>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: 0, flex: "1 1 200px" }}>
                  Garanta seu lote antes da entrega e valorize seu investimento.
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: "0 0 auto" }}>
                  <a
                    href="https://wa.me/5533999948278"
                    target="_blank" rel="noreferrer"
                    style={{
                      background: "#1B6B2F", color: "#fff",
                      padding: "14px 22px", borderRadius: 4,
                      fontSize: 13, fontWeight: 600, textDecoration: "none",
                      fontFamily: "'Barlow', sans-serif", minHeight: 44, display: "inline-flex", alignItems: "center",
                    }}
                  >
                    Falar no WhatsApp
                  </a>
                  <button
                    onClick={() => { setEmpreendimentoAtivo(e); setPage("empreendimento"); }}
                    style={{
                      background: "#CC1111", color: "#fff", border: "none",
                      padding: "14px 22px", borderRadius: 4,
                      fontSize: 13, fontWeight: 600, cursor: "pointer",
                      fontFamily: "'Barlow', sans-serif", minHeight: 44,
                    }}
                  >
                    Tenho interesse
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* ── Bairros entregues ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "1.2px", color: "#1B6B2F", textTransform: "uppercase", marginBottom: 16 }}>
            Bairros entregues
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            {EMPREENDIMENTOS.filter(e => e.status !== "Em construção").map((e) => (
              <div key={e.id}
                onClick={() => { setEmpreendimentoAtivo(e); setPage("empreendimento"); }}
                style={{
                  background: "#fff", border: "0.5px solid #e8e8e8",
                  borderRadius: 8, overflow: "hidden", cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}
                onMouseEnter={ev => { ev.currentTarget.style.transform = "translateY(-3px)"; ev.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
                onMouseLeave={ev => { ev.currentTarget.style.transform = "translateY(0)"; ev.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}
              >
                <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", overflow: "hidden", borderBottom: "2px solid #1B6B2F" }}>
                  <EmpreendimentoSlideshow empreendimento={e} activeIndex={slideIndex} />
                  <span style={{
                    position: "absolute", top: 8, right: 8,
                    background: "#1B6B2F", color: "#fff",
                    fontSize: 9, fontWeight: 700, letterSpacing: "1px",
                    textTransform: "uppercase", padding: "3px 8px", borderRadius: 2,
                  }}>Entregue</span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  <h3 style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", marginBottom: 3 }}>{e.nome}</h3>
                  <p style={{ fontSize: 11, color: "#888" }}>Ver detalhes →</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fechamento */}
        <div style={{ marginTop: 32, borderTop: "1px solid #d8e8db", paddingTop: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <p style={{ fontSize: 14, color: "#555", margin: 0 }}>
            Cada projeto entregue fortalece nossa principal marca: <strong>confiança construída ao longo do tempo.</strong>
          </p>
          <button onClick={() => setPage("contato")} style={{
            background: "none", color: "#CC1111", border: "1px solid #CC1111",
            padding: "12px 20px", borderRadius: 4, fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: "'Barlow', sans-serif", whiteSpace: "nowrap", minHeight: 44,
          }}>
            Entre em contato →
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── SEÇÕES COMPARTILHADAS ────────────────────────────────────────────────────

/**
 * SecaoSobre — seção institucional com a história de José Amaro de Carvalho.
 * Usada tanto na Home quanto na página "Sobre nós".
 *
 * Layout: foto à esquerda (sticky no desktop) + texto à direita.
 * Ao final do texto: 3 pilares institucionais (Segurança Jurídica,
 * Qualidade Executiva, Desenvolvimento Local).
 *
 * Em mobile a foto fica acima do texto (flex empilhado).
 */
function SecaoSobre() {
  const isMobile = useIsMobile();
  return (
    <section style={{ background: "#e8e2d9", padding: isMobile ? "48px 1.25rem" : "72px 2rem", fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ marginBottom: isMobile ? 28 : 40 }}>
          <div style={{ width: 40, height: 3, background: "#CC1111", marginBottom: 14 }} />
          <h2 style={{ fontSize: "clamp(26px, 5vw, 38px)", fontWeight: 700, color: "#1a1a1a", margin: 0, lineHeight: 1.15 }}>
            Nossa história
          </h2>
        </div>

        <div style={{ display: "flex", gap: isMobile ? 28 : 48, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: isMobile ? "1 1 100%" : "0 0 300px", width: isMobile ? "100%" : 300, position: isMobile ? "relative" : "sticky", top: 96, alignSelf: "flex-start" }}>
          <div style={{ borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", border: "1px solid #e0e8e2" }}>
            <img
              src={joseAmaroImg}
              alt="José Amaro de Carvalho"
              style={{ width: "100%", height: isMobile ? "auto" : 400, maxHeight: isMobile ? 320 : "none", display: "block", objectFit: "cover", objectPosition: "top center" }}
            />
          </div>
        </div>

        <div style={{ flex: "1 1 300px", minWidth: 0 }}>
          <div style={{ width: 36, height: 3, background: "#CC1111", marginBottom: 20 }} />
          <h2 style={{ fontSize: "clamp(20px, 5vw, 26px)", fontWeight: 700, color: "#1a1a1a", marginBottom: 18, lineHeight: 1.3 }}>
            José Amaro de Carvalho
          </h2>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 16 }}>
            A história de José Amaro de Carvalho, conhecido por todos como Zé Mauro, faz parte do desenvolvimento de Capelinha e região. Reconhecido pelo trabalho sério, pela visão empreendedora e pela credibilidade construída ao longo dos anos, Zé Mauro se tornou um nome forte e respeitado no setor empresarial e imobiliário regional.
          </p>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 16 }}>
            Foi com esse espírito de trabalho e compromisso que nasceu a Construtora Carvalho, empresa criada com o propósito de desenvolver empreendimentos sólidos, planejados e capazes de transformar a realidade de muitas famílias. Ao longo dos anos, a construtora se consolidou como referência no setor imobiliário, participando diretamente da construção de mais de 1.500 residências e contribuindo de forma significativa para o crescimento urbano de Capelinha.
          </p>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 16 }}>
            Entre os principais empreendimentos desenvolvidos ao longo dessa trajetória estão os bairros e loteamentos Morada Nova, Cidade Jardim, Esplanada, Jardim Aeroporto II, Nações, Grajaú, Floresta e Belvedere. Cada projeto representa mais do que infraestrutura e urbanização: representa sonhos realizados, oportunidades e qualidade de vida para centenas de famílias.
          </p>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 16 }}>
            Com o passar dos anos, a atuação empresarial de Zé Mauro também se expandiu para outros segmentos importantes da economia regional. Além da Construtora Carvalho, fazem parte dessa história empresas e empreendimentos como a Casa Carvalho, referência em materiais de construção; o Edifício Carvalho, importante centro comercial localizado no centro de Capelinha; o Posto RR – Real & Rocha, voltado para o setor de combustíveis; e a Pesadão Peças e Diesel, empresa ligada ao segmento automotivo e de peças para veículos pesados.
          </p>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 16 }}>
            Mais do que construir empreendimentos, Zé Mauro construiu uma trajetória baseada em confiança, responsabilidade e desenvolvimento. Cada empresa nasceu com o compromisso de gerar empregos, fortalecer a economia local e oferecer qualidade em produtos e serviços.
          </p>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8 }}>
            Hoje, a história continua sendo escrita com o mesmo propósito que marcou o início dessa caminhada: trabalhar com seriedade, investir no crescimento da cidade e contribuir para o desenvolvimento de Capelinha e de toda a região.
          </p>

          {/* Pilares institucionais */}
          <div style={{ marginTop: 36, borderTop: "1px solid #e0e8e2", paddingTop: 28, display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { titulo: "Segurança Jurídica",   desc: "Todos os projetos seguem rigor documental, garantindo tranquilidade para investidores e compradores." },
              { titulo: "Qualidade Executiva",  desc: "Infraestrutura planejada para durar, da base ao acabamento." },
              { titulo: "Desenvolvimento Local", desc: "Cada empreendimento contribui diretamente para o crescimento econômico e urbano de Capelinha." },
            ].map(({ titulo, desc }) => (
              <div key={titulo} style={{
                display: "flex", gap: 14, alignItems: "flex-start",
                padding: "14px 16px",
                background: "#fff",
                border: "0.5px solid #e0e8e2",
                borderLeft: "3px solid #1B6B2F",
                borderRadius: 6,
              }}>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", marginBottom: 4 }}>{titulo}</h4>
                  <p style={{ fontSize: 13, color: "#777", lineHeight: 1.65, margin: 0 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}

/**
 * SecaoServicos — grid de cards com os serviços da construtora.
 * Renderiza os itens de SERVICOS com numeração (01, 02...), descrição e lista
 * de diferenciais. Usada na Home, na página Sobre e na página Serviços.
 *
 * @param {Function} setPage - navega para contato ao clicar no CTA
 */
function SecaoServicos({ setPage }) {
  const isMobile = useIsMobile();
  return (
    <section style={{ padding: isMobile ? "48px 1.25rem" : "72px 2rem", fontFamily: "'Barlow', sans-serif", background: "#1a1a1a" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "1.5px", color: "#CC1111", textTransform: "uppercase", marginBottom: 12 }}>Serviços</div>
          <h2 style={{ fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>Soluções completas em<br />desenvolvimento urbano</h2>
          <div style={{ width: 40, height: 3, background: "#CC1111", margin: "14px auto 0" }} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, marginBottom: 40 }}>
          {SERVICOS.map(({ titulo, desc, items }, idx) => (
            <div key={titulo} style={{
              padding: "clamp(20px, 3vw, 30px) clamp(16px, 2.5vw, 26px)", background: "#242424",
              border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 8,
              borderTop: "3px solid #1B6B2F",
              boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
            }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: "rgba(255,255,255,0.08)", marginBottom: 12, lineHeight: 1 }}>
                0{idx + 1}
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 12 }}>{titulo}</h3>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.75, marginBottom: 18 }}>{desc}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {items.map(item => (
                  <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
                    <span style={{ color: "#1B6B2F", fontWeight: 700, marginTop: 1 }}>—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <button onClick={() => setPage && setPage("contato")} style={{
            background: "#CC1111", color: "#fff",
            padding: "14px 36px", border: "none", borderRadius: 4,
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            fontFamily: "'Barlow', sans-serif", minHeight: 44,
          }}>
            Fale com nossa equipe
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * CtaContato — faixa vermelha de call-to-action ao final das páginas.
 * Oferece dois caminhos: formulário de contato e WhatsApp direto.
 *
 * @param {Function} setPage - navega para a página de contato
 */
function CtaContato({ setPage }) {
  const isMobile = useIsMobile();
  return (
    <section style={{ background: "#CC1111", textAlign: "center", padding: isMobile ? "40px 1.25rem" : "64px 2rem", fontFamily: "'Barlow', sans-serif" }}>
      <h2 style={{ fontSize: "clamp(20px, 5vw, 26px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>
        Fale com a Construtora Carvalho
      </h2>
      <p style={{ fontSize: "clamp(14px, 2vw, 15px)", color: "rgba(255,255,255,0.85)", marginBottom: 28 }}>
        Nossa equipe está pronta para atender você e apresentar nossos projetos.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={() => setPage("contato")} style={{
          background: "#fff", color: "#CC1111", padding: "14px 32px",
          border: "none", borderRadius: 4, fontSize: 14, fontWeight: 600,
          cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
        }}>
          Formulário de contato
        </button>
        <a href="https://wa.me/5533999948278" target="_blank" rel="noreferrer" style={{
          background: "#1B6B2F", color: "#fff", padding: "14px 32px",
          borderRadius: 4, fontSize: 14, fontWeight: 500, textDecoration: "none",
          display: "inline-flex", alignItems: "center", fontFamily: "'Barlow', sans-serif", minHeight: 44,
        }}>
          WhatsApp
        </a>
      </div>
    </section>
  );
}

// ─── DOCUMENTOS LEGAIS ────────────────────────────────────────────────────────

/**
 * LEGAL_DOCS — textos completos dos documentos legais do site.
 * Chaves disponíveis: "termos" | "privacidade" | "lgpd" | "cookies"
 * Cada entrada tem { titulo, conteudo }.
 * Abertos via ModalLegal quando o usuário clica nos links do footer ou
 * no banner de cookies.
 */
const LEGAL_DOCS = {
  termos: {
    titulo: "Termos de Uso",
    conteudo: `Ao acessar e utilizar este site, você concorda com os presentes Termos de Uso.

1. USO DO SITE
Este site tem caráter informativo sobre os empreendimentos e serviços da Construtora Carvalho Ltda. O conteúdo disponibilizado não constitui oferta vinculante ou contrato de compra e venda.

2. PROPRIEDADE INTELECTUAL
Todos os textos, imagens, logotipos e materiais presentes neste site são de propriedade exclusiva da Construtora Carvalho Ltda e estão protegidos pela legislação de direitos autorais. É proibida a reprodução sem autorização prévia e por escrito.

3. RESPONSABILIDADE
As informações sobre empreendimentos são meramente ilustrativas. Plantas, perspectivas e especificações podem ser alteradas sem aviso prévio, conforme permitido pela legislação vigente.

4. LINKS EXTERNOS
Este site pode conter links para sites de terceiros. A Construtora Carvalho não se responsabiliza pelo conteúdo ou práticas de privacidade desses sites.

5. ALTERAÇÕES
Estes termos podem ser atualizados a qualquer momento. Recomendamos a leitura periódica desta página.

6. FORO
Fica eleito o foro da comarca de Capelinha/MG para dirimir eventuais controvérsias decorrentes deste instrumento.`,
  },
  privacidade: {
    titulo: "Política de Privacidade",
    conteudo: `A Construtora Carvalho Ltda respeita a sua privacidade e está comprometida com a proteção dos seus dados pessoais.

1. DADOS COLETADOS
Coletamos apenas os dados que você nos fornece voluntariamente por meio do formulário de contato: nome, e-mail, telefone e mensagem.

2. FINALIDADE DO USO
Os dados coletados são utilizados exclusivamente para:
• Responder às suas solicitações de contato;
• Enviar informações sobre nossos empreendimentos, mediante seu consentimento;
• Cumprir obrigações legais.

3. COMPARTILHAMENTO
Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros, exceto quando exigido por lei ou ordem judicial.

4. ARMAZENAMENTO E SEGURANÇA
Seus dados são armazenados em ambiente seguro e mantidos apenas pelo tempo necessário para as finalidades descritas.

5. SEUS DIREITOS
Você tem direito a acessar, corrigir, eliminar ou revogar o consentimento sobre seus dados a qualquer momento. Entre em contato conosco para exercer esses direitos.

6. CONTATO
Dúvidas sobre privacidade: construtoracarvalho.cap@hotmail.com`,
  },
  lgpd: {
    titulo: "LGPD — Lei Geral de Proteção de Dados",
    conteudo: `Em conformidade com a Lei nº 13.709/2018 (LGPD), a Construtora Carvalho Ltda informa:

1. CONTROLADOR DOS DADOS
Construtora Carvalho Ltda
CNPJ: 09.408.317/0001-36
Endereço: Av. Tancredo Neves, 727, Capelinha/MG, CEP 39683-532
E-mail do encarregado (DPO): construtoracarvalho.cap@hotmail.com

2. BASE LEGAL PARA O TRATAMENTO
O tratamento dos seus dados pessoais é realizado com base em:
• Consentimento (art. 7º, I): para envio de comunicações de marketing;
• Execução de contrato (art. 7º, V): para atendimento de solicitações;
• Legítimo interesse (art. 7º, IX): para melhoria dos nossos serviços.

3. DIREITOS DO TITULAR
Nos termos da LGPD, você tem direito a:
• Confirmar a existência de tratamento;
• Acessar seus dados;
• Corrigir dados incompletos ou desatualizados;
• Solicitar anonimização, bloqueio ou eliminação;
• Solicitar portabilidade;
• Revogar o consentimento a qualquer tempo.

4. COMO EXERCER SEUS DIREITOS
Envie sua solicitação para: construtoracarvalho.cap@hotmail.com
Prazo de resposta: até 15 dias úteis.

5. AUTORIDADE NACIONAL
Você também pode contatar a Autoridade Nacional de Proteção de Dados (ANPD): www.gov.br/anpd`,
  },
  cookies: {
    titulo: "Política de Cookies",
    conteudo: `Este site utiliza cookies para melhorar a sua experiência de navegação.

O QUE SÃO COOKIES?
Cookies são pequenos arquivos de texto armazenados no seu dispositivo quando você visita um site. Eles permitem que o site lembre suas preferências e melhore o desempenho.

COOKIES QUE UTILIZAMOS

• Cookies essenciais
Necessários para o funcionamento básico do site. Sem eles, o site não funciona corretamente. Não podem ser desativados.

• Cookies de desempenho
Coletam informações sobre como os visitantes usam o site (ex.: Google Analytics), de forma anônima, para nos ajudar a melhorar o conteúdo.

• Cookies de preferências
Lembram suas escolhas (ex.: preferência de idioma) para personalizar sua experiência.

GERENCIAR COOKIES
Você pode aceitar ou recusar cookies não essenciais pelo banner exibido ao acessar o site. Também pode gerenciar cookies diretamente nas configurações do seu navegador.

RETENÇÃO
Cookies de sessão são excluídos ao fechar o navegador. Cookies persistentes expiram conforme descrito em cada categoria (geralmente entre 30 dias e 2 anos).

Dúvidas: construtoracarvalho.cap@hotmail.com`,
  },
};

/**
 * ModalLegal — modal de sobreposição para exibir documentos legais.
 * Fecha ao clicar no overlay, no botão "×" ou no botão "Fechar".
 *
 * @param {{ titulo: string, conteudo: string } | null} doc - documento a exibir; null oculta o modal
 * @param {Function} onClose - callback para fechar o modal
 */
function ModalLegal({ doc, onClose }) {
  if (!doc) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.6)", display: "flex",
        alignItems: "center", justifyContent: "center",
        padding: "1rem", fontFamily: "'Barlow', sans-serif",
      }}
    >
      <div
        onClick={ev => ev.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 10, maxWidth: 680, width: "100%",
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ padding: "20px clamp(16px, 4vw, 28px) 16px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: "clamp(15px, 3vw, 18px)", fontWeight: 700, color: "#1a1a1a" }}>{doc.titulo}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#888", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "16px clamp(16px, 4vw, 28px)", overflowY: "auto", flex: 1 }}>
          {doc.conteudo.split("\n\n").map((bloco, i) => (
            <p key={i} style={{ fontSize: 14, color: "#444", lineHeight: 1.8, marginBottom: 14, whiteSpace: "pre-line" }}>{bloco}</p>
          ))}
        </div>
        <div style={{ padding: "16px 28px", borderTop: "1px solid #eee", textAlign: "right" }}>
          <button onClick={onClose} style={{
            background: "#1B6B2F", color: "#fff", border: "none",
            padding: "9px 24px", borderRadius: 4, fontSize: 13,
            fontWeight: 600, cursor: "pointer", fontFamily: "'Barlow', sans-serif",
          }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── BANNER DE COOKIES ────────────────────────────────────────────────────────

/**
 * CookieBanner — banner fixo na parte inferior da tela solicitando consentimento.
 * Persiste a escolha no localStorage ("cookies_aceitos": "sim" | "nao").
 * Não é exibido se o usuário já aceitou ou recusou anteriormente.
 *
 * @param {Function} onOpenDoc - abre o ModalLegal para "cookies" ou "privacidade"
 */
function CookieBanner({ onOpenDoc }) {
  const [visible, setVisible] = useState(() => !localStorage.getItem("cookies_aceitos"));

  if (!visible) return null;

  const aceitar = () => { localStorage.setItem("cookies_aceitos", "sim"); setVisible(false); };
  const recusar = () => { localStorage.setItem("cookies_aceitos", "nao"); setVisible(false); };

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 150,
      background: "#1a1a1a", borderTop: "3px solid #CC1111",
      padding: "14px clamp(1rem, 4vw, 2rem)", fontFamily: "'Barlow', sans-serif",
      display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
      boxShadow: "0 -4px 24px rgba(0,0,0,0.25)",
    }}>
      <p style={{ flex: 1, fontSize: 13, color: "rgba(255,255,255,0.75)", margin: 0, minWidth: 240, lineHeight: 1.6 }}>
        Usamos cookies para melhorar sua experiência. Ao continuar navegando, você concorda com nossa{" "}
        <button onClick={() => onOpenDoc("cookies")} style={{ background: "none", border: "none", cursor: "pointer", color: "#8fca9f", fontSize: 13, textDecoration: "underline", padding: 0, fontFamily: "'Barlow', sans-serif" }}>
          Política de Cookies
        </button>{" "}e{" "}
        <button onClick={() => onOpenDoc("privacidade")} style={{ background: "none", border: "none", cursor: "pointer", color: "#8fca9f", fontSize: 13, textDecoration: "underline", padding: 0, fontFamily: "'Barlow', sans-serif" }}>
          Política de Privacidade
        </button>.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={recusar} style={{
          background: "transparent", color: "rgba(255,255,255,0.5)",
          border: "1px solid rgba(255,255,255,0.2)", padding: "12px 18px",
          borderRadius: 4, fontSize: 12, cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
        }}>Recusar</button>
        <button onClick={aceitar} style={{
          background: "#CC1111", color: "#fff", border: "none",
          padding: "12px 20px", borderRadius: 4, fontSize: 12,
          fontWeight: 600, cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
        }}>Aceitar cookies</button>
      </div>
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────

/**
 * Footer — rodapé com 4 colunas: marca, navegação, legal e contato.
 * Inclui link direto para WhatsApp e barra inferior com copyright.
 *
 * @param {Function} setPage    - navega entre páginas nos links de navegação
 * @param {Function} onOpenDoc  - abre o ModalLegal para documentos legais
 */
function Footer({ setPage, onOpenDoc }) {
  const ano = new Date().getFullYear();
  return (
    <footer style={{ background: "#111", fontFamily: "'Barlow', sans-serif" }}>
      {/* Corpo do footer */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "52px clamp(1.25rem, 4vw, 2rem) 36px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 36 }}>

        {/* Coluna 1 — Marca */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <LogoIcon size={36} />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Construtora</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#CC1111" }}>Carvalho</div>
            </div>
          </div>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.7, margin: 0 }}>
            Desenvolvendo empreendimentos com qualidade e compromisso em Capelinha, Minas Gerais.
          </p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", marginTop: 14 }}>
            CNPJ: 09.408.317/0001-36<br />
            Av. Tancredo Neves, 727 — Capelinha/MG<br />
            CEP 39683-532
          </p>
        </div>

        {/* Coluna 2 — Navegação */}
        <div>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 16 }}>Navegação</h4>
          {[
            { label: "Início",           page: "inicio" },
            { label: "Sobre nós",        page: "sobre" },
            { label: "Empreendimentos",  page: "empreendimentos" },
            { label: "Serviços",         page: "servicos" },
            { label: "Contato",          page: "contato" },
          ].map(({ label, page }) => (
            <button key={page} onClick={() => setPage(page)} style={{
              display: "block", background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "rgba(255,255,255,0.5)", padding: "8px 0",
              fontFamily: "'Barlow', sans-serif", textAlign: "left",
              transition: "color 0.2s",
            }}
              onMouseEnter={e => e.target.style.color = "#fff"}
              onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.5)"}
            >{label}</button>
          ))}
        </div>

        {/* Coluna 3 — Legal */}
        <div>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 16 }}>Legal & Privacidade</h4>
          {[
            { label: "Termos de Uso",          key: "termos" },
            { label: "Política de Privacidade", key: "privacidade" },
            { label: "LGPD",                    key: "lgpd" },
            { label: "Política de Cookies",     key: "cookies" },
          ].map(({ label, key }) => (
            <button key={key} onClick={() => onOpenDoc(key)} style={{
              display: "block", background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "rgba(255,255,255,0.5)", padding: "8px 0",
              fontFamily: "'Barlow', sans-serif", textAlign: "left",
              transition: "color 0.2s",
            }}
              onMouseEnter={e => e.target.style.color = "#fff"}
              onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.5)"}
            >{label}</button>
          ))}
        </div>

        {/* Coluna 4 — Contato */}
        <div>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.5)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 16 }}>Contato</h4>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.9, margin: 0 }}>
            construtoracarvalho.cap@hotmail.com<br />
            +55 33 9994-8278<br />
            Av. Tancredo Neves, 727<br />
            Capelinha — MG, 39683-532
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            <a
              href="https://wa.me/5533999948278"
              target="_blank" rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center",
                background: "#1B6B2F", color: "#fff",
                padding: "12px 20px", borderRadius: 4,
                fontSize: 13, fontWeight: 600, textDecoration: "none",
                fontFamily: "'Barlow', sans-serif", minHeight: 44,
              }}
            >WhatsApp</a>
            <a
              href="https://www.instagram.com/constru_carvalho/"
              target="_blank" rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center",
                background: "linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)",
                color: "#fff",
                padding: "12px 20px", borderRadius: 4,
                fontSize: 13, fontWeight: 600, textDecoration: "none",
                fontFamily: "'Barlow', sans-serif", minHeight: 44,
              }}
            >Instagram</a>
          </div>
        </div>
      </div>

      {/* Barra inferior */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px clamp(1.25rem, 4vw, 2rem)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>
            © 2011–{ano} Construtora Carvalho Ltda. Todos os direitos reservados.
          </span>
          <a
            href="https://www.instagram.com/gnttecnologia/"
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", textDecoration: "none" }}
            onMouseEnter={e => e.target.style.color = "#fff"}
            onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}
          >
            Desenvolvido GNT Tech
          </a>
        </div>
      </div>
    </footer>
  );
}

// ─── PÁGINAS ──────────────────────────────────────────────────────────────────

/**
 * PageHome — página inicial (rota "inicio").
 * Composição de seções em ordem:
 *   Hero → Strip → TiraInstitucional → SecaoDiferencial →
 *   SecaoEmpreendimentos → SecaoSobre → SecaoServicos → CtaContato
 *
 * @param {Function} setPage               - navega para outra página
 * @param {Function} setEmpreendimentoAtivo - define o empreendimento para a página de detalhe
 */
function PageHome({ setPage, setEmpreendimentoAtivo }) {
  return (
    <>
      <Hero setPage={setPage} />
      <TiraInstitucional />
      <SecaoDiferencial setPage={setPage} />
      <SecaoEmpreendimentos setPage={setPage} setEmpreendimentoAtivo={setEmpreendimentoAtivo} />
      <SecaoSobre />
      <SecaoServicos setPage={setPage} />
      <CtaContato setPage={setPage} />
    </>
  );
}

/**
 * PageSobre — página "Sobre nós" (rota "sobre").
 * Reutiliza SecaoSobre e SecaoServicos com um cabeçalho escuro próprio.
 *
 * @param {Function} setPage - navega para outra página
 */
function PageSobre({ setPage }) {
  return (
    <div style={{ paddingTop: 78, fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ background: "#1a1a1a", padding: "clamp(32px, 6vw, 60px) clamp(1.25rem, 4vw, 2rem) clamp(28px, 5vw, 48px)", textAlign: "center", borderBottom: "3px solid #CC1111" }}>
        <h1 style={{ fontSize: "clamp(24px, 5vw, 36px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Sobre nós</h1>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }}>Conheça a história e os valores da Construtora Carvalho</p>
      </div>
      <SecaoSobre />
      <SecaoServicos setPage={setPage} />
      <CtaContato setPage={setPage} />
    </div>
  );
}

/**
 * PageEmpreendimentos — lista completa de empreendimentos (rota "empreendimentos").
 * Inclui filtro por tipo: Todos | Loteamento | Bairro Planejado.
 * Ao clicar em um card, define o empreendimento ativo e navega para "empreendimento".
 *
 * @param {Function} setPage               - navega para outra página
 * @param {Function} setEmpreendimentoAtivo - define o item selecionado
 */
function PageEmpreendimentos({ setPage, setEmpreendimentoAtivo }) {
  const [filtro, setFiltro] = useState("Todos");
  const [slideIndex, setSlideIndex] = useState(0);
  const tipos = ["Todos", "Loteamento", "Bairro Planejado"];
  const filtrados = filtro === "Todos" ? EMPREENDIMENTOS : EMPREENDIMENTOS.filter(e => e.tipo === filtro);

  useEffect(() => {
    const timer = window.setInterval(() => setSlideIndex((index) => index + 1), 3600);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div style={{ paddingTop: 78, fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ background: "#1a1a1a", padding: "clamp(32px, 6vw, 60px) clamp(1.25rem, 4vw, 2rem) clamp(28px, 5vw, 48px)", textAlign: "center", borderBottom: "3px solid #CC1111" }}>
        <h1 style={{ fontSize: "clamp(24px, 5vw, 36px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Empreendimentos</h1>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }}>Todos os bairros e loteamentos da Construtora Carvalho</p>
      </div>

      <div style={{ padding: "32px clamp(1.25rem, 4vw, 2rem)", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 36, flexWrap: "wrap" }}>
          {tipos.map(t => (
            <button key={t} onClick={() => setFiltro(t)} style={{
              padding: "12px 18px", borderRadius: 4, fontSize: 13, fontWeight: filtro === t ? 600 : 400,
              background: filtro === t ? "#CC1111" : "#f0f0f0",
              color: filtro === t ? "#fff" : "#555",
              border: "none", cursor: "pointer", fontFamily: "'Barlow', sans-serif",
              transition: "all 0.2s", minHeight: 44,
            }}>{t}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 20 }}>
          {filtrados.map((e) => {
            const cor = e.status === "Em construção" ? "#CC1111" : "#1B6B2F";
            return (
              <div key={e.id}
                onClick={() => { setEmpreendimentoAtivo(e); setPage("empreendimento"); }}
                style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
                onMouseEnter={ev => { ev.currentTarget.style.transform = "translateY(-3px)"; ev.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; }}
                onMouseLeave={ev => { ev.currentTarget.style.transform = "translateY(0)"; ev.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)"; }}
              >
                <div style={{ height: 100, background: `${cor}0D`, borderBottom: `2px solid ${cor}`, position: "relative", overflow: "hidden" }}>
                  <EmpreendimentoSlideshow empreendimento={e} activeIndex={slideIndex} borderColor={cor} />
                </div>
                <div style={{ padding: "16px 18px" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", marginBottom: 6 }}>{e.nome}</h3>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 10 }}>{e.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * PageEmpreendimento — detalhe de um empreendimento individual (rota "empreendimento").
 * Exibe imagem (ou placeholder), descrição, informações técnicas e CTAs.
 * Renderiza null se nenhum empreendimento estiver selecionado.
 *
 * @param {{ id, nome, tipo, status, desc, img }} empreendimento - item selecionado
 * @param {Function} setPage - navega para outra página (ex: voltar a "empreendimentos")
 */
function PageEmpreendimento({ empreendimento, setPage }) {
  const isMobile = useIsMobile();
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setSlideIndex((index) => index + 1), 3600);
    return () => window.clearInterval(timer);
  }, []);

  if (!empreendimento) return null;
  const cor = empreendimento.status === "Em construção" ? "#CC1111" : "#1B6B2F";

  return (
    <div style={{ paddingTop: 78, fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ background: "#1a1a1a", padding: isMobile ? "36px 1.25rem 28px" : "60px 2rem 48px", borderBottom: "3px solid #CC1111" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <button onClick={() => setPage("empreendimentos")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer", marginBottom: 16, fontFamily: "'Barlow', sans-serif", padding: 0 }}>
            ← Voltar aos empreendimentos
          </button>
          <span style={{ display: "inline-block", fontSize: 11, padding: "3px 10px", borderRadius: 2, background: `${cor}25`, color: "#FF7A7A", fontWeight: 500, marginBottom: 14 }}>{empreendimento.tipo}</span>
          <h1 style={{ fontSize: "clamp(22px, 5vw, 36px)", fontWeight: 700, color: "#fff" }}>{empreendimento.nome}</h1>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "28px 1.25rem" : "48px 2rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: isMobile ? 24 : 40, alignItems: "start" }}>
          {/* ── Coluna principal ── */}
          <div>
            {/* Placeholder de imagem */}
            <div style={{ background: `${cor}08`, border: `1px solid ${cor}20`, borderRadius: 8, height: 240, position: "relative", overflow: "hidden", marginBottom: 32 }}>
              <EmpreendimentoSlideshow empreendimento={empreendimento} activeIndex={slideIndex} borderColor={cor} borderRadius={8} />
            </div>

            {/* Descrição */}
            <h2 style={{ fontSize: 18, fontWeight: 600, color: "#1a1a1a", marginBottom: 10 }}>Sobre o empreendimento</h2>
            <p style={{ fontSize: 15, color: "#555", lineHeight: 1.85, marginBottom: 32 }}>
              {empreendimento.descCompleta || empreendimento.desc}
            </p>

            {/* Diferenciais */}
            {empreendimento.diferenciais && empreendimento.diferenciais.length > 0 && (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 16 }}>
                  Diferenciais
                </h3>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: 10,
                }}>
                  {empreendimento.diferenciais.map((item) => (
                    <div key={item} style={{
                      display: "flex", alignItems: "flex-start", gap: 10,
                      background: `${cor}07`,
                      border: `1px solid ${cor}22`,
                      borderRadius: 6,
                      padding: "10px 14px",
                    }}>
                      <span style={{
                        flexShrink: 0,
                        width: 18, height: 18,
                        borderRadius: "50%",
                        background: cor,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        marginTop: 1,
                      }}>
                        <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                          <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span style={{ fontSize: 13, color: "#333", lineHeight: 1.5 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Coluna lateral ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ padding: "20px", background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 8, borderTop: `3px solid ${cor}` }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", marginBottom: 12 }}>Informações</h3>
              {[
                { label: "Tipo",        valor: empreendimento.tipo },
                { label: empreendimento.status === "Em construção" ? "Previsão" : "Entrega", valor: empreendimento.ano || "—" },
                { label: "Status",      valor: empreendimento.status },
                { label: "Localização", valor: "Capelinha — MG" },
              ].map(({ label, valor }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid #f0f0f0", fontSize: 13 }}>
                  <span style={{ color: "#888" }}>{label}</span>
                  <span style={{ color: "#1a1a1a", fontWeight: 500 }}>{valor}</span>
                </div>
              ))}
            </div>

            <button onClick={() => setPage("contato")} style={{
              width: "100%", background: "#CC1111", color: "#fff",
              padding: "14px", border: "none", borderRadius: 4,
              fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
            }}>
              Tenho interesse
            </button>
            <a href="https://wa.me/5533999948278" target="_blank" rel="noreferrer" style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#1B6B2F", color: "#fff",
              padding: "14px", borderRadius: 4, fontSize: 14, fontWeight: 500,
              textDecoration: "none", fontFamily: "'Barlow', sans-serif", minHeight: 44,
            }}>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * PageServicos — página de serviços (rota "servicos").
 * Reutiliza SecaoServicos com cabeçalho próprio.
 *
 * @param {Function} setPage - navega para outra página
 */
function PageServicos({ setPage }) {
  return (
    <div style={{ paddingTop: 78, fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ background: "#1a1a1a", padding: "clamp(32px, 6vw, 60px) clamp(1.25rem, 4vw, 2rem) clamp(28px, 5vw, 48px)", textAlign: "center", borderBottom: "3px solid #CC1111" }}>
        <h1 style={{ fontSize: "clamp(24px, 5vw, 36px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Serviços</h1>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.6)" }}>Nossas áreas de atuação e especialidades</p>
      </div>
      <SecaoServicos setPage={setPage} />
      <CtaContato setPage={setPage} />
    </div>
  );
}

/**
 * PageContato — página de contato (rota "contato").
 * Layout em duas colunas: informações de contato à esquerda, formulário à direita.
 * O formulário é apenas visual (não envia dados para servidor).
 * Ao submeter, exibe mensagem de confirmação no lugar do formulário.
 *
 * Para integrar com um backend, substitua a função `submit` pelo fetch/axios desejado.
 */
function PageContato() {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", mensagem: "" });
  const [enviado, setEnviado] = useState(false);
  const isMobile = useIsMobile();

  const handle = (k) => (ev) => setForm(f => ({ ...f, [k]: ev.target.value }));
  const submit = (ev) => {
    ev.preventDefault();
    setEnviado(true);
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", fontSize: 14, minHeight: 44,
    border: "0.5px solid #ddd", borderRadius: 4, outline: "none",
    fontFamily: "'Barlow', sans-serif", background: "#fff", color: "#1a1a1a",
    boxSizing: "border-box",
  };

  return (
    <div style={{ paddingTop: 78, fontFamily: "'Barlow', sans-serif" }}>
      <div style={{ background: "#1a1a1a", padding: isMobile ? "40px 1.25rem 32px" : "60px 2rem 48px", textAlign: "center", borderBottom: "3px solid #CC1111" }}>
        <h1 style={{ fontSize: "clamp(24px, 5vw, 36px)", fontWeight: 700, color: "#fff", marginBottom: 12 }}>Contato</h1>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)" }}>Fale com nossa equipe</p>
      </div>

      <div style={{ maxWidth: 840, margin: "0 auto", padding: isMobile ? "36px 1.25rem" : "56px 2rem", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 32 : 48, alignItems: "start" }}>

        <div>
          <div style={{ width: 36, height: 3, background: "#CC1111", marginBottom: 20 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a", marginBottom: 16 }}>Fale conosco</h2>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginBottom: 28 }}>
            Nossa equipe está pronta para atender você e esclarecer qualquer dúvida sobre nossos empreendimentos.
          </p>

          {[
            { label: "Endereço", valor: "Av. Tancredo Neves, 727 — Capelinha/MG" },
            { label: "WhatsApp", valor: "+55 33 9994-8278" },
            { label: "E-mail",   valor: "construtoracarvalho.cap@hotmail.com" },
            { label: "Horário",  valor: "Seg a Sex, 7h às 17h" },
          ].map(({ label, valor }) => (
            <div key={label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#CC1111", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, color: "#444" }}>{valor}</div>
            </div>
          ))}
        </div>

        <div>
          {enviado ? (
            <div style={{ padding: 28, background: "#1B6B2F10", border: "1px solid #1B6B2F30", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1B6B2F", marginBottom: 8 }}>Mensagem enviada!</h3>
              <p style={{ fontSize: 14, color: "#555" }}>Em breve nossa equipe entrará em contato.</p>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Nome *</label>
                <input style={inputStyle} value={form.nome} onChange={handle("nome")} placeholder="Seu nome completo" required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>E-mail *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={handle("email")} placeholder="seu@email.com" required />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Telefone</label>
                <input style={inputStyle} value={form.telefone} onChange={handle("telefone")} placeholder="(67) 9 9999-9999" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Mensagem *</label>
                <textarea style={{ ...inputStyle, minHeight: 120, resize: "vertical" }} value={form.mensagem} onChange={handle("mensagem")} placeholder="Como podemos ajudar?" required />
              </div>
              <button type="submit" style={{
                background: "#CC1111", color: "#fff", padding: "14px",
                border: "none", borderRadius: 4, fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "'Barlow', sans-serif", minHeight: 44,
              }}>
                Enviar mensagem
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── APP PRINCIPAL ────────────────────────────────────────────────────────────

/**
 * App — componente raiz da aplicação.
 *
 * Estado global:
 *   page                 {string}      - página ativa (ver lista no topo do arquivo)
 *   empreendimentoAtivo  {object|null} - empreendimento selecionado para a página de detalhe
 *   modalDoc             {object|null} - documento legal aberto no ModalLegal
 *
 * Comportamentos:
 *   - Rola para o topo ao trocar de página
 *   - Carrega a fonte Barlow via Google Fonts dinamicamente
 *   - Renderiza CookieBanner e ModalLegal fora do fluxo das páginas
 */
export default function App() {
  const [page, setPage] = useState("inicio");
  const [empreendimentoAtivo, setEmpreendimentoAtivo] = useState(null);
  const [modalDoc, setModalDoc] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const openDoc = (key) => setModalDoc(LEGAL_DOCS[key]);

  const renderPage = () => {
    switch (page) {
      case "inicio":          return <PageHome setPage={setPage} setEmpreendimentoAtivo={setEmpreendimentoAtivo} />;
      case "sobre":           return <PageSobre setPage={setPage} />;
      case "empreendimentos": return <PageEmpreendimentos setPage={setPage} setEmpreendimentoAtivo={setEmpreendimentoAtivo} />;
      case "empreendimento":  return <PageEmpreendimento empreendimento={empreendimentoAtivo} setPage={setPage} />;
      case "servicos":        return <PageServicos setPage={setPage} />;
      case "contato":         return <PageContato />;
      default:                return <PageHome setPage={setPage} setEmpreendimentoAtivo={setEmpreendimentoAtivo} />;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa" }}>
      <Navbar activePage={page} setPage={setPage} />
      <main>{renderPage()}</main>
      <Footer setPage={setPage} onOpenDoc={openDoc} />
      <CookieBanner onOpenDoc={openDoc} />
      <ModalLegal doc={modalDoc} onClose={() => setModalDoc(null)} />
    </div>
  );
}
